import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Message pushed onto the shared timer-toast subject. */
export interface TimerToastMessage {
    text: string;
    /** Optional undo callback — when omitted, the toast renders as a
     *  plain notification without an UNDO button. */
    undoFn?: () => void;
}

/**
 * Cross-component pipe for `timer-toast` messages.
 *
 * `TimerToastComponent` is mounted at the layout level — `<timer-toast>`
 * inside `default.component.html` for the browser site, and inside
 * `desktop-shell.component.html` for the Electron app. The two layouts
 * are mutually exclusive at any given route, so exactly one toast
 * instance is in the DOM at any time.
 *
 * Components that need to surface a toast inject this service and call
 * `show(...)` — they don't need a direct reference to the mounted
 * `TimerToastComponent`. The component subscribes on init and forwards
 * each emission to its own `show(...)` method.
 *
 * Local-only — no HTTP, no broadcast, no persistence.
 */
@Injectable({ providedIn: 'root' })
export class TimerToastService {
    private readonly _messages$ = new Subject<TimerToastMessage>();

    /** Stream that `TimerToastComponent` subscribes to. */
    readonly messages$: Observable<TimerToastMessage> = this._messages$.asObservable();

    /** Push a message to whichever timer-toast is currently mounted. If
     *  none is mounted (e.g. user is on a page that doesn't include it),
     *  the message is silently dropped — same behaviour as before this
     *  service existed. */
    show(text: string, undoFn?: () => void): void {
        this._messages$.next({ text, undoFn });
    }
}
